import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteOrganisation } from "../api/delete-organisations";

type OrganisationRowActionsProps = {
  organisationId: string;
};

export function OrganisationRowActions(props: OrganisationRowActionsProps) {
  const {
    mutateAsync: deleteOrganisation,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useDeleteOrganisation();
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteOrganisation({ id: props.organisationId });
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/organisations/${props.organisationId}/overview`);
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleEdit}>View</DropdownMenuItem>

          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this organisation?
          </DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
